import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  CalendarIcon,
  TicketIcon,
  UserGroupIcon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  BuildingStorefrontIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { getToken } from '../../utils/constants';
import { DASHBOARD_URL } from '../../config/appConfig';
import useAuth from '../../hooks/useAuth';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { logout } from '../../features/auth/authSlice';
import { useToast } from '../common/Toast';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  requiresAuth?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { user } = useAuth();
  const isLoggedIn = !!getToken();
  
  // Check if user is already a provider
  const isProvider = user?.roles?.includes('provider_admin') || 
                     user?.roles?.includes('provider_staff') || 
                     (user?.providerMemberships?.length ?? 0) > 0;
  const dashboardUrl = DASHBOARD_URL;

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    dispatch(logout());
    toast.success("You've been logged out successfully.");
    onClose();
    navigate('/login');
  };

  const navItems: NavItem[] = [
    { to: '/', icon: HomeIcon, label: 'Home' },
    // { to: '/search', icon: MagnifyingGlassIcon, label: 'Search' },
    { to: '/providers', icon: UserGroupIcon, label: 'Providers' },
    { to: '/booking-lookup', icon: TicketIcon, label: 'Track Booking' },
    // { to: '/queue', icon: QueueListIcon, label: 'Queue Status' },
    { to: '/bookings', icon: CalendarIcon, label: 'My Bookings', requiresAuth: true },
    { to: '/profile', icon: UserCircleIcon, label: 'Profile', requiresAuth: true },
    { to: '/settings', icon: Cog6ToothIcon, label: 'Settings', requiresAuth: true },
  ];

  const filteredNavItems = navItems.filter(
    (item) => !item.requiresAuth || (item.requiresAuth && isLoggedIn)
  );

  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div
      className={`fixed inset-0 z-50 transition-all duration-300 ${open ? 'visible' : 'invisible pointer-events-none'}`}
      aria-hidden={!open}
    >
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <aside
        className={`absolute left-0 top-0 h-full w-[280px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${open ? 'translate-x-0' : '-translate-x-full'}`}
        role="navigation"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <Link to="/" onClick={onClose} className="flex items-center space-x-2">
            <img
              src="/appointigo.png"
              alt="Appointigo"
              className="h-8 w-auto object-contain"
            />
          </Link>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close sidebar"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* User Profile Section (if logged in) */}
        {isLoggedIn && (
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-accent-50">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg shadow-md ${user?.profile?.avatar ? '' : 'bg-gradient-to-br from-primary-600 to-accent-600'}`}>
                {user?.profile?.avatar ? (
                  <img
                    src={user.profile.avatar}
                    alt={user.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  getUserInitial()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {user?.email || user?.phone || ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.to);
              
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-600 to-accent-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer Actions */}
        <div className="px-3 py-4 border-t border-gray-100">
          {/* Provider/Business Section */}
          {isProvider ? (
            /* Provider Dashboard Link - only for logged in providers */
            <a
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl font-medium text-primary-600 hover:bg-primary-50 transition-colors mb-2"
            >
              <Squares2X2Icon className="w-5 h-5" />
              <span>Dashboard</span>
            </a>
          ) : (
            /* List Your Business - for non-providers (logged in or not) */
            <Link
              to="/list-a-business"
              onClick={onClose}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl font-medium text-accent-600 bg-accent-50 hover:bg-accent-100 border border-accent-200 transition-colors mb-2"
            >
              <BuildingStorefrontIcon className="w-5 h-5" />
              <span>List Your Business</span>
            </Link>
          )}
          {isLoggedIn ? (
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl font-medium text-red-600 hover:bg-red-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              <span>Logout</span>
            </button>
          ) : (
            <div className="space-y-2">
              <Link
                to="/login"
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-600 to-accent-600 hover:shadow-lg transition-all"
              >
                <ArrowLeftOnRectangleIcon className="w-5 h-5" />
                <span>Sign In</span>
              </Link>
              <Link
                to="/register"
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl font-semibold text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                <span>Create Account</span>
              </Link>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default Sidebar;
